<template>
  <div class="bg-white rounded-lg shadow p-6">
    <div class="flex items-center mb-4">
      <img
        v-if="userInfo.avatar"
        :src="userInfo.avatar"
        class="w-16 h-16 rounded-full object-cover mr-4"
        alt="头像"
      />
      <div v-else class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
        <i class="fas fa-user text-2xl text-blue-500"></i>
      </div>
      <div>
        <h3 class="text-lg font-semibold text-gray-800">{{ userInfo.name || '用户' }}</h3>
        <p class="text-sm text-gray-500">UID: {{ userInfo.uid }}</p>
      </div>
    </div>

    <el-descriptions :column="2" border>
      <el-descriptions-item label="UID" :span="2">
        <span class="font-mono text-sm">{{ userInfo.uid || '-' }}</span>
      </el-descriptions-item>
      <el-descriptions-item label="UnionID" :span="2">
        <span class="font-mono text-sm">{{ userInfo.unionid || '-' }}</span>
      </el-descriptions-item>
      <el-descriptions-item label="手机号">
        <el-tag size="small" type="info">{{ userInfo.contact_mobile || '未绑定' }}</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="VIP状态">
        <el-tag :type="userInfo.is_vip ? 'success' : 'info'" size="small">
          {{ userInfo.is_vip ? 'VIP' : '非VIP' }}
        </el-tag>
        <span v-if="userInfo.is_vip && userInfo.vip_expire_time" class="text-xs text-gray-500 ml-2">
          ({{ formatDate(userInfo.vip_expire_time) }})
        </span>
      </el-descriptions-item>
      <el-descriptions-item label="现场抽奖权益">
        <el-tag :type="userInfo.is_live_vip ? 'warning' : 'info'" size="small">
          {{ userInfo.is_live_vip ? '有' : '无' }}
        </el-tag>
        <span v-if="userInfo.is_live_vip && userInfo.live_vip_expire_time" class="text-xs text-gray-500 ml-2">
          ({{ formatDate(userInfo.live_vip_expire_time) }})
        </span>
      </el-descriptions-item>
      <el-descriptions-item label="封禁状态">
        <el-tag :type="userInfo.is_banned ? 'danger' : 'success'" size="small">
          {{ userInfo.is_banned ? '已封禁' : '正常' }}
        </el-tag>
        <span v-if="userInfo.is_banned && userInfo.ban_reason" class="text-xs text-red-500 ml-2">
          原因: {{ userInfo.ban_reason }}
        </span>
      </el-descriptions-item>
      <el-descriptions-item label="注册时间">
        {{ formatDate(userInfo.create_time) }}
      </el-descriptions-item>
      <el-descriptions-item label="最后登录">
        {{ formatDate(userInfo.last_login_time) }}
      </el-descriptions-item>
      <el-descriptions-item label="创建中活动">
        <el-tag size="small">{{ userInfo.creating_count || 0 }}</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="进行中活动">
        <el-tag size="small">{{ userInfo.ongoing_count || 0 }}</el-tag>
      </el-descriptions-item>
    </el-descriptions>

    <!-- 跟进记录列表 -->
    <div v-if="visitLogList && visitLogList.length > 0" class="mt-6">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-md font-semibold text-gray-800">跟进记录</h4>
        <el-button type="primary" size="small" @click="showAddDialog">
          <i class="fas fa-plus mr-1"></i>添加回访
        </el-button>
      </div>
      <el-table
        :data="visitLogList"
        style="width: 100%"
        stripe
        border
        size="small"
      >
        <el-table-column prop="visit_time" label="时间" width="180">
          <template #default="scope">
            {{ formatDate(scope.row.visit_time) }}
          </template>
        </el-table-column>
        <el-table-column prop="operator" label="跟进人" width="120">
          <template #default="scope">
            <span class="text-sm">{{ scope.row.operator || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="content" label="说明">
          <template #default="scope">
            <span class="text-sm">{{ scope.row.content || '-' }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 无记录时显示添加按钮 -->
    <div v-else class="mt-6 text-center">
      <el-button type="primary" size="small" @click="showAddDialog">
        <i class="fas fa-plus mr-1"></i>添加回访记录
      </el-button>
    </div>

    <!-- 添加回访记录弹窗 -->
    <el-dialog
      v-model="addDialogVisible"
      title="添加回访记录"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form>
        <el-form-item label="说明">
          <el-input
            v-model="visitRemark"
            type="textarea"
            :rows="4"
            placeholder="请输入回访内容..."
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitVisit">
          保存
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getAdminApiUrl } from "@/api/luckdraw.js";
import { getUserToken, getUserInfo } from "@/utils/auth.js";

export default {
  name: 'UserProfile',
  props: {
    uid: {
      type: [String, Number],
      required: true
    },
    userInfo: {
      type: Object,
      default: () => ({})
    },
    visitLogList: {
      type: Array,
      default: () => []
    }
  },
  emits: ['refresh-visit-log'],
  data() {
    return {
      addDialogVisible: false,
      visitRemark: '',
      submitting: false
    };
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    showAddDialog() {
      this.visitRemark = '';
      this.addDialogVisible = true;
    },
    async submitVisit() {
      if (!this.visitRemark.trim()) {
        this.$message.warning('请输入回访内容');
        return;
      }

      // 获取当前登录用户信息
      const userInfo = getUserInfo();
      const operator = userInfo?.username || userInfo?.name || userInfo?.nickname || '';

      this.submitting = true;
      try {
        const token = getUserToken();
        const response = await fetch(getAdminApiUrl('/user/visit/add'), {
          method: 'POST',
          headers: {
            'Token': `${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            uid: this.uid,
            content: this.visitRemark.trim(),
            operator: operator
          })
        });
        const result = await response.json();
        if (result.sta === 0) {
          this.$message.success('保存成功');
          this.addDialogVisible = false;
          this.$emit('refresh-visit-log');
        } else {
          this.$message.error(result.msg || '保存失败');
        }
      } catch (error) {
        console.error('保存回访记录失败:', error);
        this.$message.error('保存失败');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
